import to from 'await-catch'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from '../../../components/ui/button'
import useIsMobile from '../../../hooks/useIsMobile'
import AppointmentSummaryCard from './AppointmentSummaryCard'
import { buildConfirmAppointmentParams } from './buildConfirmAppointmentParams'
import { posConfirmAppointment } from '../../../api'
import { BackTitle } from '../selectAppointment/SelectAppointment'
import { ChevronLeft } from '../../../assets/icons'
import BackPageHeader from '../../../components/ui/header/BackPageHeader'

const ConfirmMultipleAppointment = ({
  onBack,
  onEditTurn,
  onMultipleConfirmed,
  setAppointmentsBookingResult,
  cartTurns = [],
  clinics = [],
  patient,
  auth,
  showLoader,
  hideLoader,
  addFlashMessage
}) => {
  const isMobile = useIsMobile()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resolveClinicName = turn => {
    const clinic = clinics.find(
      item =>
        String(item.codigoEmpresa) === String(turn?.clinica) ||
        String(item.value) === String(turn?.clinica) ||
        String(item.id) === String(turn?.clinica)
    )

    return clinic?.nombre || clinic?.label || turn?.nombreClinica || turn?.clinica || '-'
  }

  const resolveErrorMessage = err => {
    if (typeof err?.response?.data === 'string' && err.response.data.trim()) {
      return err.response.data
    }

    if (
      typeof err?.response?.data === 'object' &&
      typeof err.response.data?.message === 'string' &&
      err.response.data.message.trim()
    ) {
      return err.response.data.message
    }

    return 'Error al agendar el turno'
  }

  const handleConfirm = async () => {
    if (!cartTurns.length) {
      addFlashMessage('No hay turnos seleccionados para confirmar')
      return
    }

    if (isSubmitting) return

    setIsSubmitting(true)
    showLoader()

    try {
      const firebaseToken = localStorage.getItem('fcmToken') || ''
      const turnsResult = await Promise.all(
        cartTurns.map(async turn => {
          const params = buildConfirmAppointmentParams({ turn, patient, auth })
          const [err] = await to(
            posConfirmAppointment(params, firebaseToken, { skipErrorMessage: true })
          )

          return {
            idTurno: turn.idTurno,
            hasError: !!err,
            errorMessage: err ? resolveErrorMessage(err) : ''
          }
        })
      )

      setAppointmentsBookingResult({ turns: turnsResult })

      if (onMultipleConfirmed) {
        const confirmedCount = turnsResult.filter(result => !result.hasError).length
        onMultipleConfirmed(confirmedCount)
      }
    } catch (error) {
      addFlashMessage('Ocurrió un error al confirmar los turnos')
    } finally {
      hideLoader()
      setIsSubmitting(false)
    }
  }

  return (
    <Container>
      <Content>
				<BackPageHeader onBack={onBack} showBack={!isMobile} title="Agendar turnos" />

        <ListSection>
          {cartTurns.map(turn => (
            <AppointmentSummaryCard
              key={turn.idTurno}
              turn={turn}
              clinicName={resolveClinicName(turn)}
              onEdit={onEditTurn}
              showEdit={true}
            />
          ))}
        </ListSection>
      </Content>

      <ActionBar>
        <Button
          label={'Confirmar turnos'}
          $variant="primary"
          size={isMobile ? 'mobile' : 'xlarge'}
          onClick={handleConfirm}
          disabled={cartTurns.length === 0 || isSubmitting}
          style={{ width: '100%' }}
        />
      </ActionBar>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 80vh;
  width: 80vw;
  padding: 20px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    width: 100%;
    padding: 16px 16px 24px 16px;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: ${props => props.theme.colors.text.titleText};

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    font-size: 24px;
  }
`

const ListSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 560px;
`

const ActionBar = styled.div`
  width: 360px;
  margin-top: 24px;

  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    position: sticky;
    bottom: 90px;
    width: 100%;
    padding-top: 16px;
    padding-bottom: 8px;
    background: ${props => props.theme.colors.brand.white};
  }
`

export default ConfirmMultipleAppointment
